<script setup lang="ts">
import { ref, watch } from 'vue'
import { debounce } from 'lodash'
import { useVuelidate } from '@vuelidate/core'
import { required, helpers } from '@vuelidate/validators'
import { VButton, VIcon, VLoading, VSection } from '@/modules/shared/components'
import { useAuthStore } from '@/modules/auth/stores/auth-store'
import { useNotificationStore } from '@/modules/shared/stores/notification-store'
import { useMessagingStore } from '../stores/messaging-store'
import RecipientPicker from './RecipientPicker.vue'
import RichTextEditor from './RichTextEditor.vue'
import type { CreateMessagePayload, MessageReceiverAttribute, RecipientPreview } from '../types'

defineProps<{
  submitting: boolean
  submitError: string | null
}>()

const emit = defineEmits<{
  submit: [payload: CreateMessagePayload]
  cancel: []
}>()

const authStore = useAuthStore()
const messagingStore = useMessagingStore()
const notificationStore = useNotificationStore()

const subject = ref('')
const body = ref('')
const copyMe = ref(false)
const receivers = ref<MessageReceiverAttribute[]>([])
const files = ref<File[]>([])
const fileInput = ref<HTMLInputElement | null>(null)
const showRecipientList = ref(false)
const recipientPreview = ref<RecipientPreview | null>(null)
const loadingPreview = ref(false)

const fetchPreview = debounce(async (value: MessageReceiverAttribute[]) => {
  if (value.length === 0) {
    recipientPreview.value = null
    return
  }
  loadingPreview.value = true
  try {
    recipientPreview.value = await messagingStore.previewRecipients(value)
  } catch {
    notificationStore.enqueue('error', 'Failed to preview recipients')
  } finally {
    loadingPreview.value = false
  }
}, 400)

watch(receivers, (value) => fetchPreview(value), { deep: true })

const hasContent = helpers.withMessage('Body is required', (value: string) => {
  return value.replace(/<[^>]*>/g, '').trim().length > 0
})

const rules = {
  subject: { required },
  body: { hasContent },
}

const v$ = useVuelidate(rules, { subject, body }, { $lazy: true })

const openFileDialog = () => {
  fileInput.value?.click()
}

const onFileChange = (event: Event) => {
  const target = event.target as HTMLInputElement
  if (target.files) {
    files.value = [...files.value, ...Array.from(target.files)]
  }
}

const removeFile = (index: number) => {
  files.value.splice(index, 1)
}

const handleSubmit = async () => {
  const valid = await v$.value.$validate()
  if (!valid) return
  if (receivers.value.length === 0) return

  emit('submit', {
    subject: subject.value,
    body: body.value,
    copy_me: copyMe.value,
    message_receivers_attributes: receivers.value,
    files: files.value.length > 0 ? files.value : undefined,
  })
}
</script>

<template>
  <div class="grid grid-cols-1 gap-8 lg:grid-cols-3">
    <!-- Left column: compose form -->
    <div class="lg:col-span-2">
      <VSection label="Compose Message">
        <div class="space-y-5">
          <!-- Email-like header block -->
          <div class="overflow-hidden rounded-lg border border-gray-200 bg-white">
            <!-- From row -->
            <div class="flex items-center border-b border-gray-100 px-4 py-2.5">
              <span class="w-20 shrink-0 text-sm font-semibold text-gray-500">From</span>
              <span class="text-sm text-gray-700">{{ authStore.current_user.name }}</span>
            </div>

            <!-- To row -->
            <div class="border-b border-gray-100 px-4 py-2.5">
              <div class="flex items-start">
                <span class="w-20 shrink-0 pt-0.5 text-sm font-semibold text-gray-500">To</span>
                <div class="min-w-0 flex-1">
                  <RecipientPicker v-model="receivers" />
                  <p v-if="receivers.length === 0 && v$.$dirty" class="mt-1 text-sm text-red-500">
                    At least one recipient group is required
                  </p>
                </div>
              </div>
            </div>

            <!-- Subject row -->
            <div class="flex items-center px-4 py-2.5">
              <span class="w-20 shrink-0 text-sm font-semibold text-gray-500">Subject</span>
              <input
                v-model="subject"
                type="text"
                class="flex-1 border-none bg-transparent p-0 text-sm text-gray-900 placeholder-gray-400 focus:ring-0"
                placeholder="Enter subject..."
              />
            </div>
            <p v-if="v$.subject.$error" class="px-4 pb-2 text-sm text-red-500">
              {{ v$.subject.$errors[0]?.$message }}
            </p>
          </div>

          <!-- Rich text body -->
          <RichTextEditor
            v-model="body"
            placeholder="Begin typing your message here..."
            :error="v$.body.$error ? String(v$.body.$errors[0]?.$message) : undefined"
          />

          <!-- Attachments -->
          <div class="border-t border-gray-200 pt-4">
            <div class="flex items-center gap-3">
              <VIcon name="Paperclip" class="h-4 w-4 text-gray-400" />
              <span class="text-sm font-medium text-gray-700">Attachments</span>
              <button type="button" class="text-sm text-[#3b88af] hover:text-[#3b88af]/80" @click="openFileDialog">
                Select files...
              </button>
              <input ref="fileInput" type="file" multiple class="hidden" @change="onFileChange" />
            </div>
            <div v-if="files.length > 0" class="ml-7 mt-2 space-y-1">
              <div v-for="(file, i) in files" :key="i" class="flex items-center gap-2 text-sm text-gray-600">
                <span>{{ file.name }}</span>
                <button type="button" class="text-gray-400 hover:text-red-500" @click="removeFile(i)">
                  <VIcon name="X" class="h-3 w-3" />
                </button>
              </div>
            </div>
          </div>

          <!-- Send copy checkbox -->
          <label class="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              v-model="copyMe"
              class="rounded border-gray-300 text-[#3b88af] focus:ring-[#3b88af]"
            />
            Send copy to &lt;{{ authStore.current_user.email }}&gt;
          </label>

          <!-- Error -->
          <p v-if="submitError" class="text-sm text-red-500">{{ submitError }}</p>

          <!-- Action buttons -->
          <div class="flex items-center gap-3 pt-2">
            <VButton :click="handleSubmit" variant="v-blue" size="lg" :disabled="submitting">
              {{ submitting ? 'Sending...' : 'Send Message' }}
            </VButton>
            <VButton @click="emit('cancel')" size="lg">Cancel</VButton>
          </div>
        </div>
      </VSection>
    </div>

    <!-- Right column: recipient preview sidebar -->
    <div class="lg:col-span-1">
      <div class="sticky top-8">
        <VSection label="Message will be sent to">
          <div v-if="loadingPreview" class="flex justify-center py-4">
            <VLoading />
          </div>
          <div v-else-if="recipientPreview">
            <div class="mb-3 flex items-center justify-between">
              <span class="text-sm font-medium text-gray-700">
                {{ recipientPreview.count }} recipient{{ recipientPreview.count !== 1 ? 's' : '' }}
              </span>
              <button
                v-if="recipientPreview.recipients.length > 0"
                type="button"
                class="text-sm text-[#3b88af] hover:text-[#3b88af]/80"
                @click="showRecipientList = !showRecipientList"
              >
                {{ showRecipientList ? 'Hide' : 'Show' }}
              </button>
            </div>
            <ul v-if="showRecipientList" class="max-h-96 space-y-1 overflow-y-auto">
              <li v-for="r in recipientPreview.recipients" :key="r.email" class="truncate text-sm text-gray-600">
                {{ r.email }}
              </li>
            </ul>
          </div>
          <p v-else class="text-sm italic text-gray-400">Select recipients to preview</p>
        </VSection>
      </div>
    </div>
  </div>
</template>
